import {useImmer} from "use-immer";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {GiustificativoRecord} from "@commons/mapping/GiustificativoRecord";
import {CoppiaTimbrature} from "@commons/mapping/CoppiaTimbrature";
import {GestionePlus} from "@commons/services/presenze_calcolo/GestionePlus";
import {cloneDeep, merge, omit} from "lodash";
import {GiuFormValues, setupDefaultValues as setupGiuValues} from "../localMapping/PresenzeGiustificativoEx";
import {useCallback} from "react";
import {Timbrature} from "@commons/services/presenze_calcolo/sub_calcolo/Timbrature";
import {PresenzeGiustificativo} from "@commons/mapping/PresenzeGiustificativo";

const numMaxCoppie = 5;

export interface PresenzeFormValues extends PresenzeGiornataPlusRecord, GiuFormValues {
}

export const initTimbrature = (source: CoppiaTimbrature[], target: CoppiaTimbrature[], nMaxCoppie: number = numMaxCoppie, maskEffettive?: number) => {
    source.forEach((coppia, index) => {
        if (!CoppiaTimbrature.isNull(coppia)) {
            const newCoppia = CoppiaTimbrature.factory(coppia);
            if (maskEffettive) {
                const pos = index * 2;
                newCoppia.utilizzaEntrata = (maskEffettive & (1 << pos)) > 0;
                newCoppia.utilizzaUscita = (maskEffettive & (1 << (pos + 1))) > 0;
            }
            target.push(newCoppia);
        }
    });

    while (target.length < numMaxCoppie) {
        const newCoppia = CoppiaTimbrature.factory();
        target.push(newCoppia);
    }
}

// Metodo per inizializzare i valori del form sulla base della giornata indicata in <day>
export const setupFormValues = (day: PresenzeGiornataPlusRecord, giustificativi: GiustificativoRecord[]): PresenzeFormValues => {
    const valuesPreviste: CoppiaTimbrature[] = [];
    const valuesEffettive: CoppiaTimbrature[] = [];
    const valuesEffettiveOriginali: CoppiaTimbrature[] = [];
    const valuesArrotondate: CoppiaTimbrature[] = [];

    if (day.previste) {
        initTimbrature(day.previste, valuesPreviste, 4);
    }

    if (day.effettive) {
        initTimbrature(day.effettive, valuesEffettive);
    }

    if (day.effettiveOriginali) {
        initTimbrature(day.effettiveOriginali, valuesEffettiveOriginali, numMaxCoppie, day.maskEffettive);
    }

    if (day.arrotondate) {
        initTimbrature(day.arrotondate, valuesArrotondate);
    }

    const effettiveManualiFlat = GestionePlus.adeguaEffManuali(day, cloneDeep(day.effettiveManualiFlat));

    const clone = cloneDeep(day);
    return {
        ...PresenzeGiornataPlusRecord.factory(merge(clone, {
            previste: valuesPreviste,
            effettive: valuesEffettive,
            effettiveOriginali: valuesEffettiveOriginali,
            arrotondate: valuesArrotondate,
            effettiveManualiFlat,

        })),
        ...setupGiuValues(clone, giustificativi),
    };
};

export const form2PresenzeGiornata = (draft: PresenzeGiornataPlusRecord, payload: PresenzeFormValues) => {
    draft.anomalia = payload.anomalia;
    draft.warning = payload.warning;
    draft.giornataBloccata = payload.giornataBloccata;

    draft.previste = Timbrature.defragStrisciata(payload.previste, false);
    draft.arrotondate = Timbrature.defragStrisciata(payload.arrotondate, false);

    // Normalizzo codGiu ed eseguo defrag su giustificativi
    draft.causaliHH = [];
    payload.giustificativiHHGiornata.forEach(giustificativo => {
        giustificativo && draft.causaliHH.push(PresenzeGiustificativo.factory(omit(giustificativo, 'giustificativo')))
    });
    draft.causaliHH = PresenzeGiornataPlusRecord.defrag(draft.causaliHH);

    // Normalizzo codGiu ed eseguo defrag su giustificativi
    draft.causaliQI = [];
    payload.giustificativiQIGiornata.forEach(giustificativo => {
        giustificativo && draft.causaliQI.push(PresenzeGiustificativo.factory(omit(giustificativo, 'giustificativo')))
    });
    draft.causaliQI = PresenzeGiornataPlusRecord.defrag(draft.causaliQI);
}

export const useFormData = (dayOriginale: PresenzeGiornataPlusRecord,
                            listaGiustificativi: GiustificativoRecord[]) => {
    const [defaultValues, updateDraft] = useImmer<PresenzeFormValues>(() => setupFormValues(dayOriginale, listaGiustificativi));

    /**
     * Allinea defaultValues con gli elementi della form
     */
    const allineaDefaultValues = useCallback((payload: PresenzeFormValues,
                                              dirtyValues: Partial<PresenzeGiornataPlusRecord>) => {
        updateDraft(draft => form2PresenzeGiornata(draft, payload));
        /*
                updateDraft(draft => {
                    draft.anomalia = payload.anomalia;
                    draft.giornataBloccata = payload.giornataBloccata;

                    draft.previste = Timbrature.defragStrisciata(payload.previste, false);
                    draft.arrotondate = Timbrature.defragStrisciata(payload.arrotondate, false);

                    // Normalizzo codGiu ed eseguo defrag su giustificativi
                    draft.causaliHH = [];
                    payload.giustificativiHHGiornata.forEach(giustificativo => {
                        giustificativo && draft.causaliHH.push(PresenzeGiustificativo.factory(omit(giustificativo, 'giustificativo')))
                    });
                    draft.causaliHH = PresenzeGiornataPlusRecord.defrag(draft.causaliHH);

                    // Normalizzo codGiu ed eseguo defrag su giustificativi
                    draft.causaliQI = [];
                    payload.giustificativiQIGiornata.forEach(giustificativo => {
                        giustificativo && draft.causaliQI.push(PresenzeGiustificativo.factory(omit(giustificativo, 'giustificativo')))
                    });
                    draft.causaliQI = PresenzeGiornataPlusRecord.defrag(draft.causaliQI);
                });
        */
    }, [updateDraft]);

    const allineaCausali = useCallback((payload: PresenzeFormValues) => {
        //const payload = getValues();
        //const dValues = FormUtils.getDirtyFields(dirtyFields, payload) as Partial<PresenzeFormValues>;
        updateDraft(draft => {
            draft.anomalia = payload.anomalia;
            draft.giornataBloccata = payload.giornataBloccata;

            // Normalizzo codGiu ed eseguo defrag su giustificativi
            draft.causaliHH = [];
            payload.giustificativiHHGiornata.forEach(giustificativo => {
                giustificativo && draft.causaliHH.push(PresenzeGiustificativo.factory(omit(giustificativo, 'giustificativo')))
            });
            draft.causaliHH = PresenzeGiornataPlusRecord.defrag(draft.causaliHH);

            // Normalizzo codGiu ed eseguo defrag su giustificativi
            draft.causaliQI = [];
            payload.giustificativiQIGiornata.forEach(giustificativo => {
                giustificativo && draft.causaliQI.push(PresenzeGiustificativo.factory(omit(giustificativo, 'giustificativo')))
            });
            draft.causaliQI = PresenzeGiornataPlusRecord.defrag(draft.causaliQI);
        });
    }, [updateDraft]);

    return {defaultValues, updateDraft, allineaDefaultValues, allineaCausali};
}